/**
 * Object 3D (OBJECTS round, 2026-08): PROCEDURAL prototype bodies, instanced per TYPE
 * through the SAME pipeline as doors/windows/casework (protoKey → one prototype + a
 * transform per element). V1 is deliberately the procedural tier of the OBJECT ANATOMY
 * decision — honest simple masses per kind; the type's reserved `meshRef` slot is where
 * a sanitized manufacturer/AI mesh lands in the import round (being a type param it
 * already forks protoKeyFor, so mesh attachment needs no instancing rework).
 *
 * The PLAN never reads this geometry — the authored symbol (planSymbols.ts) is the plan
 * representation, so objectSolid emits NO projLines and no outline prism. Sections and
 * elevations project the prototype's true 3D edges through the generic instanced
 * emission in section.ts (exactly like casework).
 *
 * Prototype local frame (objectKinds.ts convention): x across the width centered on 0,
 * y = depth 0..d toward the front, z up from the instance base. Rim-mounted kinds
 * (kitchen/bath sinks) build their body hanging from `rimHeightFt` above the base.
 */
import type { ParamValue, Pt2, ResolvedElement } from '@aj/bim-kernel'
import { objectAnchorPoint, WALL_MOUNT_DEFAULT_FT, OBJECT_KINDS_WALL_MOUNT, isStyledPlumbingKind, plumbingStyleOf, applianceUnitOf, CASEWORK_TOE_FT } from '@aj/bim-kernel'
import { buildPlumbingProto, plumbingZSpanFt, plumbingOnCounter, plumbingCounterCutIn } from './plumbing3d'
import { plumbingWidthIn } from '@aj/bim-kernel'

const WALL_MOUNT_KINDS = new Set<string>(OBJECT_KINDS_WALL_MOUNT)
import { MeshBuilder } from '../../extrude'
import { buildVehicle } from './vehicles'
import { buildTrampoline } from './trampoline'
import { buildFirePit } from './firePit'
import { buildFountain } from './fountain'
import { buildTrashCart, buildUmbrella, buildChaise } from './yardPieces'
import { buildGasMeter, buildWaterMeterBox, WATER_BOX_TOP_FRAC } from './utilities'
import type { SolidGroup } from '../opening/protos'
import { buildFreestandingTub } from './freestandingTub'
import { buildPlantProxy } from './plants'
import { buildElevatorInto } from './elevator3d'
import { featureEdges } from '../../edges'
import { getMeshAsset } from '../../meshAsset'
import { caseworkTopZ } from '../casework/generate3d'
import type { OpeningProto } from '../opening/protos'
import { foundationGradeAt } from '../wall/footing'
import type { ElementSolid } from '../wall/generate3d'

/** the object's vertical extent RELATIVE to its instance base, from the type params.
 *  SURFACE HOSTING round (2026-08-24): `hosted` — when the instance base IS a real
 *  host surface (a countertop, another object's top, a floor), a rim-kind object's
 *  rim sits AT that surface (rim offset 0) and its bowl hangs BELOW it by the full
 *  `heightFt` — no clamp, since "below the instance base" is exactly where a sink
 *  bowl belongs once the base IS the counter (down into the cabinet cavity), unlike
 *  the unhosted case where the base is the FLOOR and `Math.max(0, …)` guards
 *  against a nonsensical below-floor bowl for a badly-set rimHeightFt. `rimHeightFt`
 *  itself is a level-relative height GUESS for the unhosted case only, unchanged
 *  for every already-placed unhosted sink. */
/** SHOWER TRIM: the wall-mount height, the kind's firm default when unset */
function wallMountFt(p: Record<string, ParamValue>): number {
  const v = p.mountHeightFt as number
  return v > 0 ? v : (WALL_MOUNT_DEFAULT_FT[String(p.objectKind)] ?? 4)
}

/** COOKTOPS: how far a cooktop stands proud of the counter it sits in (feet) */
export function cooktopAboveFt(p: Record<string, ParamValue>): number {
  const s = String(p.cooktopStyle || 'gas')
  return s === 'induction' || s === 'electric' ? 0.03 : 0.12
}

/** COOKTOPS: the opening a cooktop cuts in the countertop it sits in (inches, local frame:
 *  x across centred on 0, y from the back) — the body's own size less a 3/4" lip all round,
 *  so the deck or glass laps the counter */
export function cooktopCounterCutIn(p: Record<string, ParamValue>): Pt2[] {
  const w = ((p.widthFt as number) || 2.5) * 12, d = ((p.depthFt as number) || 1.75) * 12
  const x = w / 2 - 0.75, y0 = 0.75, y1 = d - 0.75
  return [[-x, y0], [x, y0], [x, y1], [-x, y1]]
}

/** KITCHEN APPLIANCES round: a pro range's backguard (Wolf's 10" standard) */
const RANGE_BACKGUARD_FT = 10 / 12

/** the cooktop's zones across the width (local x), shared by the 3D body and the plan
 *  symbol so they always agree: burner COLUMNS (two burners deep each; an odd count
 *  gets a single centered burner in its last column) and the griddle/charbroiler
 *  zones on the right */
export function rangeCooktopLayout(p: Record<string, ParamValue>): {
  burnerCols: { x0: number; x1: number; n: 1 | 2 }[]
  griddle: [number, number] | null
  charbroiler: [number, number] | null
} {
  const w = (p.widthFt as number) || 2.5
  const burners = Math.max(1, Math.round((p.burners as number) || 4))
  const extra = String(p.cooktopExtra || 'none')
  const side = 0.1
  let xR = w / 2 - side
  const zone = (): [number, number] => { const z: [number, number] = [xR - 1, xR]; xR -= 1.04; return z }
  const charbroiler = extra === 'charbroiler' || extra === 'griddleCharbroiler' ? zone() : null
  const griddle = extra === 'griddle' || extra === 'griddleCharbroiler' ? zone() : null
  const xL = -w / 2 + side
  const cols = Math.ceil(burners / 2)
  const cw = Math.max(0.3, (xR - xL) / cols)
  const burnerCols = Array.from({ length: cols }, (_, i) => ({
    x0: xL + i * cw, x1: xL + (i + 1) * cw,
    n: (i === cols - 1 && burners % 2 === 1 ? 1 : 2) as 1 | 2,
  }))
  return { burnerCols, griddle, charbroiler }
}

export function objectZSpan(p: Record<string, ParamValue>, hosted = false): { z0: number; z1: number } {
  const kind = String(p.objectKind || 'toilet')
  const h = (p.heightFt as number) || 2.5
  // PLUMBING STYLES (2026-10-07): a styled fixture's extent comes from its style
  if (isStyledPlumbingKind(kind)) {
    const st = plumbingStyleOf(kind, p)
    if (st) return plumbingZSpanFt(kind, st, p, hosted)
  }
  if (kind === 'kitchenSink' || kind === 'bathSink') {
    if (hosted) return { z0: -h, z1: 0 }
    const rim = (p.rimHeightFt as number) || 3
    return { z0: Math.max(0, rim - h), z1: rim }
  }
  if (kind === 'range') return { z0: 0, z1: h + RANGE_BACKGUARD_FT }   // body + backguard
  // COOKTOPS: the sink rule — at the counter when seated on one, else hung from its rim
  if (kind === 'cooktop') {
    const above = cooktopAboveFt(p)
    if (hosted) return { z0: -h, z1: above }
    const rim = (p.rimHeightFt as number) || 3
    return { z0: Math.max(0, rim - h), z1: rim + above }
  }
  // SHOWER TRIM: wall-mounted — the valve centers on its mount height, the
  // showerhead's arm sits AT it with the head hanging below
  if (kind === 'showerHead') { const m = wallMountFt(p); return { z0: m - h, z1: m } }
  // every other wall-mounted kind (shower valve, tankless heater, mini-split head,
  // boiler, return grille) centers on its mount height
  if (WALL_MOUNT_KINDS.has(kind)) { const m = wallMountFt(p); return { z0: m - h / 2, z1: m + h / 2 } }
  if (kind === 'woodStove') return { z0: 0, z1: h + 2 }   // body + its flue collar run
  // DERIVED HOOKUPS round: a microwave drawer sits under the counter, its top at 34"
  if (kind === 'microwaveDrawer') return { z0: 34 / 12 - h, z1: 34 / 12 }
  // SITE UTILITIES (2026-10-07): the water meter box hangs below grade, its lid just proud
  if (kind === 'waterMeterBox') return { z0: -h, z1: h * WATER_BOX_TOP_FRAC }
  return { z0: 0, z1: h }
}

/** SURFACE HOSTING round: the TOP of a freestanding object, in absolute world
 *  feet — for when ANOTHER object hosts onto this one (a faucet on a
 *  pre-manufactured vanity object). Mirrors objectSolid's own zInst/zSpan math
 *  exactly (never a second hand-derived copy). */
export function objectTopZ(el: ResolvedElement): number | null {
  if (!el.level) return null
  const zInst = el.level.elevationFt + ((el.params.heightOffsetFt as number) || 0)
  // PLUMBING STYLES: a washstand / pedestal / trough hosts a faucet on its DECK
  const kind = String(el.typeParams.objectKind || '')
  const st = isStyledPlumbingKind(kind) ? plumbingStyleOf(kind, el.typeParams) : null
  if (st?.deck) return zInst + st.deck / 12
  return zInst + objectZSpan(el.typeParams).z1
}

export function buildObjectProto(el: ResolvedElement, key: string): OpeningProto {
  const p = el.typeParams as Record<string, ParamValue>
  const kind = String(p.objectKind || 'toilet')
  const w = (p.widthFt as number) || 2
  const d = (p.depthFt as number) || 2
  const h = (p.heightFt as number) || 2.5
  // PLUMBING STYLES (2026-10-07): every plumbing fixture is its style's real body now
  if (isStyledPlumbingKind(kind) && plumbingStyleOf(kind, p)) return buildPlumbingProto(el, key)

  // PHOTO-TO-3D round (docs/bim-plan/17 §4.5): the reserved `meshRef` slot,
  // finally consumed — a registered imported mesh IS the prototype, with its
  // featureEdges pre-baked at intake (never recomputed here). Not yet
  // registered (the fetch is in flight, or a stale ref) → fall through to the
  // procedural switch; 'imported' hits the default box — the honest placeholder
  // until the asset arrives and the client triggers one regenerate (the
  // deferred-terrain precedent). protoKeyFor already forks the instancing
  // cache on meshRef (a type param), so mesh and placeholder never share a key
  // ONCE the ref differs — and the regenerate rebuilds this proto in place.
  const meshRef = typeof p.meshRef === 'string' ? p.meshRef : ''
  if (meshRef) {
    const asset = getMeshAsset(meshRef)
    if (asset) {
      return {
        key,
        solid: { positions: asset.positions, normals: asset.normals, indices: asset.indices },
        glass: null,
        edges: asset.edges,
        solidMaterialId: typeof p.surfaceMaterial === 'string' && p.surfaceMaterial ? p.surfaceMaterial : null,
        uvs: asset.uvs,
        meshRef,
      }
    }
  }

  const b = new MeshBuilder()
  // VEHICLES round: a car's glasshouse rides the proto's translucent glass channel
  let glassB: MeshBuilder | null = null
  // SITE UTILITIES: per-part colours on the one solid (OpeningProto.solidGroups)
  let solidGroups: SolidGroup[] = []
  // FIRE PIT: a builder that supplies its own edge set (smooth spheres have no feature edges)
  let customEdges: Float32Array | null = null
  const box = (x0: number, x1: number, y0: number, y1: number, zA: number, zB: number) => {
    if (x1 - x0 < 1e-4 || y1 - y0 < 1e-4 || zB - zA < 1e-4) return
    prism([[x0, y0], [x1, y0], [x1, y1], [x0, y1]], zA, zB)
  }
  /** vertical prism over a convex CCW polygon — sides + top/bottom fans */
  const prism = (poly: Pt2[], zA: number, zB: number) => {
    const n = poly.length
    const bot = poly.map((q) => [q[0], q[1], zA])
    const top = poly.map((q) => [q[0], q[1], zB])
    for (let i = 2; i < n; i++) {
      b.addTri(top[0], top[i - 1], top[i], [0, 0, 1])
      b.addTri(bot[0], bot[i], bot[i - 1], [0, 0, -1])
    }
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      const nx = poly[j][1] - poly[i][1], ny = poly[i][0] - poly[j][0]
      const len = Math.hypot(nx, ny) || 1
      const nn = [nx / len, ny / len, 0]
      b.addTri(bot[i], bot[j], top[j], nn)
      b.addTri(bot[i], top[j], top[i], nn)
    }
  }
  /** a horizontal cylinder along local X (axis 'x', centered at y=c1, z=cz) or local
   *  Y (axis 'y', centered at x=c1, z=cz), from a to bb — handles, knobs, bars */
  const cyl = (axis: 'x' | 'y', c1: number, cz: number, r: number, a: number, bb: number, segs = 10) => {
    if (bb - a < 1e-4 || r < 1e-4) return
    const pt = (t: number, ang: number): number[] => {
      const u = Math.cos(ang) * r, v = Math.sin(ang) * r
      return axis === 'x' ? [t, c1 + u, cz + v] : [c1 + u, t, cz + v]
    }
    const nrm = (ang: number): number[] => (axis === 'x' ? [0, Math.cos(ang), Math.sin(ang)] : [Math.cos(ang), 0, Math.sin(ang)])
    const ax = axis === 'x' ? [1, 0, 0] : [0, 1, 0]
    const ca = axis === 'x' ? [a, c1, cz] : [c1, a, cz]
    const cb = axis === 'x' ? [bb, c1, cz] : [c1, bb, cz]
    for (let i = 0; i < segs; i++) {
      const a0 = (Math.PI * 2 * i) / segs, a1 = (Math.PI * 2 * (i + 1)) / segs
      const am = (a0 + a1) / 2
      b.addTri(pt(a, a0), pt(bb, a0), pt(bb, a1), nrm(am))
      b.addTri(pt(a, a0), pt(bb, a1), pt(a, a1), nrm(am))
      b.addTri(cb, pt(bb, a1), pt(bb, a0), ax)
      b.addTri(ca, pt(a, a0), pt(a, a1), ax.map((q) => -q))
    }
  }
  /** n-gon approximating an ellipse at (cx, cy) — CCW */
  const ellipsePoly = (cx: number, cy: number, rx: number, ry: number, segs = 14): Pt2[] => {
    const pts: Pt2[] = []
    for (let i = 0; i < segs; i++) {
      const a = (Math.PI * 2 * i) / segs
      pts.push([cx + Math.cos(a) * rx, cy + Math.sin(a) * ry])
    }
    return pts
  }
  /** COOKTOPS: a thin printed ring on glass (an induction / radiant zone) */
  const rimRing = (cx: number, cy: number, r: number, zA: number, zB: number) => {
    const segs = 20, t = 0.02
    for (let i = 0; i < segs; i++) {
      const a0 = (Math.PI * 2 * i) / segs, a1 = (Math.PI * 2 * (i + 1)) / segs
      prism([
        [cx + Math.cos(a0) * (r - t), cy + Math.sin(a0) * (r - t)],
        [cx + Math.cos(a0) * r, cy + Math.sin(a0) * r],
        [cx + Math.cos(a1) * r, cy + Math.sin(a1) * r],
        [cx + Math.cos(a1) * (r - t), cy + Math.sin(a1) * (r - t)],
      ], zA, zB)
    }
  }
  /** four rim walls of thickness t around the footprint, over [zA, zB] */
  const rimWalls = (x0: number, x1: number, y0: number, y1: number, t: number, zA: number, zB: number) => {
    box(x0, x1, y0, y0 + t, zA, zB)               // back
    box(x0, x1, y1 - t, y1, zA, zB)               // front
    box(x0, x0 + t, y0 + t, y1 - t, zA, zB)       // left
    box(x1 - t, x1, y0 + t, y1 - t, zA, zB)       // right
  }

  switch (kind) {
    case 'toilet': {
      const tankD = Math.min(0.75, d * 0.32)
      box(-w * 0.45, w * 0.45, 0, tankD, 0.8, h)                       // tank
      box(-w * 0.3, w * 0.3, 0, tankD + 0.1, 0, 0.9)                   // base/trapway
      const ry = (d - tankD - 0.08) / 2
      prism(ellipsePoly(0, tankD + 0.04 + ry, w * 0.38, ry), 0.55, 1.35)  // bowl + seat
      break
    }
    case 'bathSink': {
      const rim = (p.rimHeightFt as number) || 3
      box(-w / 2, w / 2, 0, d, rim - 0.15, rim)                        // deck
      prism(ellipsePoly(0, d * 0.52, w * 0.3, d * 0.3), Math.max(0, rim - h), rim - 0.1)  // bowl shell
      break
    }
    case 'kitchenSink': {
      const rim = (p.rimHeightFt as number) || 3
      box(-w / 2, w / 2, 0, d, rim - 0.1, rim)                         // rim deck
      const basins = Math.max(1, Math.min(2, Math.round((p.basins as number) || 1)))
      const inset = 0.12
      const y0 = inset + 0.15, y1 = d - inset
      const zLow = Math.max(0, rim - h)
      if (basins === 2) {
        box(-w / 2 + inset, -0.06, y0, y1, zLow, rim - 0.1)
        box(0.06, w / 2 - inset, y0, y1, zLow, rim - 0.1)
      } else {
        box(-w / 2 + inset, w / 2 - inset, y0, y1, zLow, rim - 0.1)
      }
      break
    }
    // FREESTANDING TUB (2026-10-06): the lofted oval shell + its floor-mounted filler
    case 'tubFreestanding': {
      buildFreestandingTub(w, d, h, b, p.tubFiller !== false)
      break
    }
    case 'tub': {
      box(-w / 2, w / 2, 0, d, 0, 0.25)                                // floor
      rimWalls(-w / 2, w / 2, 0, d, 0.25, 0.25, h)                     // shell
      break
    }
    case 'shower': {
      box(-w / 2, w / 2, 0, d, 0, Math.min(0.1, h))                    // pan
      rimWalls(-w / 2, w / 2, 0, d, 0.15, 0, h)                        // curb
      break
    }
    // ---- FURNITURE (2026-09-30): schematic masses. Local frame: back-center anchor,
    // y = 0 against the wall (a bed's headboard, a sofa's back), +y into the room ----
    case 'sofa': {
      const seatH = Math.min(h * 0.5, 1.45), arm = Math.min(0.5, w * 0.12), back = Math.min(0.75, d * 0.3)
      box(-w / 2 + arm, w / 2 - arm, back, d, 0, seatH)                // seat
      box(-w / 2, w / 2, 0, back, 0, h)                                // back
      box(-w / 2, -w / 2 + arm, back, d, 0, Math.min(h, seatH + 0.65)) // arms
      box(w / 2 - arm, w / 2, back, d, 0, Math.min(h, seatH + 0.65))
      break
    }
    case 'sectional': {
      const seatH = Math.min(h * 0.5, 1.45), sd = Math.min(3, d, w / 2), back = 0.75
      const left = p.sectionalHand !== 'right'
      const rx0 = left ? -w / 2 : w / 2 - sd, rx1 = left ? -w / 2 + sd : w / 2
      box(-w / 2, w / 2, back, sd, 0, seatH)                           // main seat
      box(-w / 2, w / 2, 0, back, 0, h)                                // main back
      box(left ? rx0 + back : rx0, left ? rx1 : rx1 - back, sd, d, 0, seatH)  // return seat
      box(left ? rx0 : rx1 - back, left ? rx0 + back : rx1, back, d, 0, h)    // return back
      break
    }
    case 'bed': {
      const mw = (p.mattressWidthFt as number) || w - 0.33, md = (p.mattressDepthFt as number) || d - 0.42
      const hb = (p.headboardFt as number) ?? 0.25
      const top = h, mattT = Math.min(0.85, top * 0.45)
      box(-w / 2, w / 2, hb, d, 0, top - mattT)                        // frame / box spring
      box(-mw / 2, mw / 2, hb, hb + md, top - mattT, top)              // mattress
      if (hb > 0.01) box(-w / 2, w / 2, 0, hb, 0, top + 1.5)           // headboard
      break
    }
    case 'dresser':
    case 'nightstand': {
      box(-w / 2, w / 2, 0, d - 0.03, 0.25, h)                         // case
      box(-w / 2 + 0.03, w / 2 - 0.03, d - 0.03, d, 0.3, h - 0.05)     // drawer fronts
      box(-w / 2, w / 2, 0, d - 0.05, 0, 0.25)                         // plinth
      break
    }
    case 'areaRug':
    case 'runner': {
      box(-w / 2, w / 2, 0, d, 0, Math.max(0.02, h))                   // RUGS: a flat pad
      break
    }
    case 'coffeeTable':
    case 'diningTable':
    case 'desk': {
      const topT = 0.12, lg = 0.18, inset = 0.15
      box(-w / 2, w / 2, 0, d, h - topT, h)                            // top
      for (const [lx, ly] of [[-w / 2 + inset, inset], [w / 2 - inset - lg, inset], [-w / 2 + inset, d - inset - lg], [w / 2 - inset - lg, d - inset - lg]]) {
        box(lx, lx + lg, ly, ly + lg, 0, h - topT)                     // legs
      }
      break
    }
    // ---- LIVING + GAME ROOM (2026-10-01) ----
    case 'loungeChair': {
      // an upholstered armchair: a plinth, a loose seat cushion between two arms, a
      // tall back cushion leaning against the back frame
      const arm = Math.min(0.5, w * 0.17), back = Math.min(0.65, d * 0.22)
      const seatH = Math.min(h * 0.55, 1.5), armH = Math.min(h, seatH + 0.6)
      box(-w / 2, w / 2, 0, d, 0, 0.35)                                // plinth
      box(-w / 2 + arm, w / 2 - arm, back, d - 0.05, 0.35, seatH)      // seat cushion
      box(-w / 2, w / 2, 0, back, 0.35, h)                             // back frame
      box(-w / 2 + arm, w / 2 - arm, back, back + 0.45, seatH, h - 0.15)  // back cushion
      box(-w / 2, -w / 2 + arm, back, d, 0.35, armH)                   // arms
      box(w / 2 - arm, w / 2, back, d, 0.35, armH)
      break
    }
    case 'poolTable': {
      // rails + cushions around the slate bed, a deep apron, six turned legs; the long
      // axis runs along local x (width)
      const rail = Math.min(0.55, w * 0.07, d * 0.1), topT = 0.2, apron = 0.75
      box(-w / 2 + rail, w / 2 - rail, rail, d - rail, h - topT - 0.15, h - 0.12)   // bed
      rimWalls(-w / 2, w / 2, 0, d, rail, h - topT, h)                 // rails
      box(-w / 2 + 0.15, w / 2 - 0.15, 0.15, d - 0.15, h - topT - apron, h - topT)  // apron / body
      const lg = 0.4
      for (const lx of [-w / 2 + 0.3, -lg / 2, w / 2 - 0.3 - lg]) {        // corners + the middle pair
        for (const ly of [0.3, d - 0.3 - lg]) box(lx, lx + lg, ly, ly + lg, 0, h - topT - apron)
      }
      break
    }
    case 'pingPongTable': {
      // a 1" top on two folding frames, the net across the middle with its posts
      const topT = 1 / 12
      box(-w / 2, w / 2, 0, d, h - topT, h)                            // playing top
      for (const fx of [-w / 4, w / 4]) {
        box(fx - 0.06, fx + 0.06, 0.4, d - 0.4, 0.15, h - topT)        // frame uprights
        box(fx - 0.6, fx + 0.6, 0.35, 0.47, 0, 0.12)                    // feet
        box(fx - 0.6, fx + 0.6, d - 0.47, d - 0.35, 0, 0.12)
      }
      box(-0.02, 0.02, -0.5, d + 0.5, h, h + 0.5)                      // net
      box(-0.05, 0.05, -0.55, -0.45, h - 0.15, h + 0.5)                // posts
      box(-0.05, 0.05, d + 0.45, d + 0.55, h - 0.15, h + 0.5)
      break
    }
    case 'diningChair': {
      const seat = Math.min(1.5, h * 0.5), lg = 0.12
      box(-w / 2, w / 2, 0.1, d, seat - 0.12, seat)                    // seat
      box(-w / 2, w / 2, 0, 0.12, seat, h)                             // back
      for (const [lx, ly] of [[-w / 2, 0], [w / 2 - lg, 0], [-w / 2, d - lg], [w / 2 - lg, d - lg]]) box(lx, lx + lg, ly, ly + lg, 0, seat - 0.12)
      break
    }
    case 'barStool': {
      const r = Math.min(w, d) / 2
      prism(ellipsePoly(0, d / 2, r * 0.8, r * 0.8, 16), 0, 0.06)      // base ring
      prism(ellipsePoly(0, d / 2, 0.08, 0.08, 10), 0.06, h - 0.1)      // post
      prism(ellipsePoly(0, d / 2, r, r, 18), h - 0.1, h)               // seat
      break
    }
    case 'deskChair': {
      const r = Math.min(w, d) / 2, seat = Math.min(1.6, h * 0.45)
      prism(ellipsePoly(0, d / 2, r * 0.9, r * 0.9, 10), 0, 0.12)      // star base
      prism(ellipsePoly(0, d / 2, 0.08, 0.08, 10), 0.12, seat - 0.2)   // gas lift
      prism(ellipsePoly(0, d / 2 + 0.05, r * 0.85, r * 0.8, 18), seat - 0.2, seat)  // seat
      box(-r * 0.7, r * 0.7, 0.05, 0.25, seat, h)                      // back
      break
    }
    // ---- FIT-OUT CATALOG (2026-09-30): shower glass, fireplaces + stoves, HVAC ----
    case 'showerDoor':
    case 'showerPanel': {
      box(-w / 2, w / 2, 0, Math.max(d, 0.03), 0, h)                    // glass
      if (kind === 'showerDoor') {
        // the pull at the strike end (opposite the hinge), on the face it swings toward
        const right = p.showerDoorHinge === 'right'
        const x0 = right ? -w / 2 + 0.08 : w / 2 - 0.12, x1 = right ? -w / 2 + 0.12 : w / 2 - 0.08
        const inSwing = p.showerDoorSwing === 'in'
        box(x0, x1, inSwing ? -0.08 : d, inSwing ? 0 : d + 0.08, h * 0.45, h * 0.6)  // pull
      }
      break
    }
    case 'fireplaceLinear': {
      box(-w / 2, w / 2, 0, d, 0, h)                                   // firebox surround
      box(-w / 2 + 0.25, w / 2 - 0.25, d - 0.02, d + 0.01, h * 0.25, h * 0.75)  // glass face
      break
    }
    case 'fireplaceWood': {
      box(-w / 2, w / 2, 0, d, 0, h)                                   // firebox
      box(-w / 2 - 0.3, w / 2 + 0.3, d, d + 1.5, 0, 0.1)               // hearth
      break
    }
    case 'woodStove': {
      box(-w / 2, w / 2, 0.15, d, 0.5, h)                              // body on legs
      for (const [lx, ly] of [[-w / 2 + 0.1, 0.25], [w / 2 - 0.1, 0.25], [-w / 2 + 0.1, d - 0.1], [w / 2 - 0.1, d - 0.1]]) {
        box(lx - 0.04, lx + 0.04, ly - 0.04, ly + 0.04, 0, 0.5)        // legs
      }
      prism(ellipsePoly(0, d * 0.4, 0.25, 0.25), h, h + 2)             // flue pipe
      break
    }
    case 'waterHeater': {
      prism(ellipsePoly(0, d / 2, w / 2, d / 2, 18), 0, h)             // tank
      prism(ellipsePoly(0, d / 2, 0.13, 0.13), h, h + 0.4)             // flue / vent
      break
    }
    case 'condenser':
    case 'miniSplitOutdoor': {
      box(-w / 2, w / 2, 0, d, 0, h)                                   // cabinet
      if (kind === 'condenser') prism(ellipsePoly(0, d / 2, w * 0.38, d * 0.38, 16), h, h + 0.04)  // fan grille
      else prism(ellipsePoly(-w * 0.12, d + 0.01, h * 0.38, 0.01, 14), h * 0.12, h * 0.88)       // front fan
      break
    }
    case 'elevator':
      // RESIDENTIAL ELEVATOR (2026-10-02): rails, carriage, cab, folding gate and (ELEVATOR
      // RIDE, 2026-10-20) the car's button panel — elevator3d.ts, shared with the walk's ride
      solidGroups = buildElevatorInto(p, w, d, h, b)
      break
    case 'poolEquipment': {
      // POOL DESIGNER round: a 4" slab with generic pump, filter, heater and controls
      const pad = 4 / 12
      box(-w / 2, w / 2, 0, d, 0, pad)
      const eh = Math.max(1, h - pad)
      const x = (f: number) => -w / 2 + w * f
      const cy = d / 2
      // pump: a low housing with its motor
      box(x(0.05), x(0.22), cy - d * 0.2, cy + d * 0.2, pad, pad + eh * 0.35)
      prism(ellipsePoly(x(0.27), cy, Math.min(0.35, d * 0.15), Math.min(0.35, d * 0.15), 14), pad, pad + eh * 0.45)
      // filter: a tall round tank
      prism(ellipsePoly(x(0.45), cy, Math.min(1, d * 0.32), Math.min(1, d * 0.32), 18), pad, pad + eh * 0.85)
      // heater: a cabinet
      box(x(0.6), x(0.82), cy - d * 0.3, cy + d * 0.3, pad, pad + eh)
      // controls: a panel at the back
      box(x(0.86), x(0.97), 0.05, 0.4, pad, pad + eh)
      break
    }
    case 'floorRegister': {
      box(-w / 2, w / 2, 0, d, 0, Math.max(h, 0.02))                   // grille plate
      break
    }
    case 'showerValve': case 'showerHead':
    case 'tanklessWaterHeater': case 'miniSplitHead': case 'boiler': case 'returnGrille':
    case 'wallTV': {
      const { z0, z1 } = objectZSpan(p)
      if (kind === 'wallTV') {
        // LIVING + GAME ROOM round: a slim panel on a low-profile wall mount
        const zc = (z0 + z1) / 2, standoff = Math.max(0.02, d - 0.12)
        box(-w * 0.2, w * 0.2, 0, standoff, zc - Math.min(0.6, (z1 - z0) * 0.3), zc + Math.min(0.6, (z1 - z0) * 0.3))  // mount
        box(-w / 2, w / 2, standoff, d, z0, z1)                        // panel
      } else if (kind === 'showerValve') {
        const zc = (z0 + z1) / 2
        box(-w / 2, w / 2, 0, 0.04, z0, z1)                            // trim plate
        box(-0.035, 0.035, 0.04, d * 0.55, zc - 0.035, zc + 0.035)     // stem
        box(-0.035, w * 0.45, d * 0.55, d, zc - 0.03, zc + 0.03)       // lever
      } else if (kind === 'showerHead') {
        const r = w / 2
        box(-0.035, 0.035, 0, d - r, z1 - 0.07, z1)                    // arm, off the wall
        box(-0.035, 0.035, d - r - 0.035, d - r + 0.035, z0 + 0.06, z1)  // drop to the head
        prism(ellipsePoly(0, d - r, r, r), z0, z0 + 0.08)              // head
      } else {
        box(-w / 2, w / 2, 0, d, z0, z1)                               // wall-hung cabinet
      }
      break
    }
    case 'furnace':
    case 'airHandler': {
      box(-w / 2, w / 2, 0, d, 0, h)                                   // cabinet
      box(-w / 2 + 0.05, w / 2 - 0.05, d, d + 0.02, h * 0.1, h * 0.55)  // access panel
      break
    }
    case 'cooktop': {
      // COOKTOPS (2026-10-15): built at the rim (the counter top) like a sink — the burner
      // box hangs `h` below it into the cabinet, the top sits just proud. Gas: continuous
      // grates over sealed burners; induction / radiant: one glass plate with its zones
      // ringed; a pro rangetop: a deeper deck with knobs on its front face. Zones from the
      // SAME layout the range and the plan symbol use (rangeCooktopLayout).
      const rim = (p.rimHeightFt as number) || 3
      const style = String(p.cooktopStyle || 'gas')
      const lay = rangeCooktopLayout(p)
      const yB = 0.12, yF = d - (style === 'rangetop' ? 0.12 : 0.1)
      box(-w / 2 + 0.08, w / 2 - 0.08, 0.08, d - 0.08, rim - h, rim)   // burner box below the counter
      if (style === 'induction' || style === 'electric') {
        box(-w / 2, w / 2, 0, d, rim, rim + 0.02)                      // the glass top
        for (const c of lay.burnerCols) {
          const ys = c.n === 2 ? [yB + (yF - yB) * 0.28, yB + (yF - yB) * 0.72] : [(yB + yF) / 2]
          const r = Math.min((c.x1 - c.x0) / 2 - 0.05, (yF - yB) / (c.n === 2 ? 4 : 2) - 0.03)
          for (const by of ys) rimRing((c.x0 + c.x1) / 2, by, r, rim + 0.02, rim + 0.026)
        }
        // the touch-control strip at the front
        box(-w * 0.3, w * 0.3, d - 0.12, d - 0.04, rim + 0.02, rim + 0.024)
        break
      }
      box(-w / 2, w / 2, 0, d, rim, rim + 0.03)                        // stainless deck
      if (style === 'rangetop') {
        // knobs on the front face, below the deck
        const knobXs: number[] = []
        for (const c of lay.burnerCols) {
          if (c.n === 2) knobXs.push(c.x0 + (c.x1 - c.x0) * 0.3, c.x0 + (c.x1 - c.x0) * 0.7)
          else knobXs.push((c.x0 + c.x1) / 2)
        }
        for (const z of [lay.griddle, lay.charbroiler]) if (z) knobXs.push((z[0] + z[1]) / 2)
        box(-w / 2, w / 2, d - 0.04, d, rim - 0.42, rim)               // the front panel
        for (const kx of knobXs) {
          cyl('y', kx, rim - 0.21, 0.08, d, d + 0.06)
          cyl('y', kx, rim - 0.21, 0.05, d + 0.06, d + 0.12)
        }
      }
      const top = rim + 0.03
      for (const c of lay.burnerCols) {
        rimWalls(c.x0 + 0.02, c.x1 - 0.02, yB, yF, 0.04, top, top + 0.09)   // grate frame
        box((c.x0 + c.x1) / 2 - 0.02, (c.x0 + c.x1) / 2 + 0.02, yB, yF, top + 0.05, top + 0.09)
        const ys = c.n === 2 ? [yB + (yF - yB) * 0.27, yB + (yF - yB) * 0.73] : [(yB + yF) / 2]
        for (const by of ys) {
          box(c.x0 + 0.02, c.x1 - 0.02, by - 0.02, by + 0.02, top + 0.05, top + 0.09)
          prism(ellipsePoly((c.x0 + c.x1) / 2, by, 0.17, 0.17, 16), top, top + 0.04)
          prism(ellipsePoly((c.x0 + c.x1) / 2, by, 0.11, 0.11, 16), top + 0.04, top + 0.06)
        }
      }
      if (lay.griddle) box(lay.griddle[0], lay.griddle[1], yB, yF, top, top + 0.05)
      if (lay.charbroiler) {
        const [x0, x1] = lay.charbroiler
        rimWalls(x0, x1, yB, yF, 0.04, top, top + 0.09)
        for (let i = 1; i < 8; i++) { const x = x0 + ((x1 - x0) * i) / 8; box(x - 0.012, x + 0.012, yB, yF, top + 0.03, top + 0.08) }
      }
      // gas cooktops: knobs on the deck at the front right (a rangetop has them on its face)
      if (style === 'gas') {
        const n = lay.burnerCols.reduce((t, c) => t + c.n, 0)
        for (let i = 0; i < n; i++) {
          const kx = -w / 2 + 0.25 + i * Math.min(0.28, (w - 0.5) / Math.max(1, n))
          if (kx > w / 2 - 0.2) break
          cyl('x', yF + 0.04, top + 0.03, 0.03, kx - 0.04, kx + 0.04)
        }
      }
      break
    }
    case 'range': {
      // KITCHEN APPLIANCES round (2026-10-01, Matt: "make it look Wolf quality"): a pro
      // dual-fuel range — recessed toe, stainless body, a proud control panel with a
      // knob per burner/zone, heavy continuous grates over sealed burners, infrared
      // griddle / charbroiler zones, one oven (30"/36") or two (48"+), each with a
      // tubular door handle on standoffs, and a 10" backguard
      const front = d - 0.12                                           // door plane
      const toe = 0.33, deck = h - 0.06
      box(-w / 2 + 0.04, w / 2 - 0.04, 0.08, front - 0.1, 0, toe)       // recessed toe
      box(-w / 2, w / 2, 0, front, toe, deck)                          // body
      box(-w / 2, w / 2, 0, d, deck, h)                                // cooktop deck
      box(-w / 2, w / 2, 0, RANGE_BACKGUARD_FT * 0.3, h, h + RANGE_BACKGUARD_FT)  // backguard
      const panelZ0 = h - 0.5, panelZ1 = h - 0.06
      box(-w / 2, w / 2, front, d, panelZ0, panelZ1)                   // control panel
      const lay = rangeCooktopLayout(p)
      // knobs: one per burner, one per griddle/charbroiler zone
      const knobXs: number[] = []
      for (const c of lay.burnerCols) {
        if (c.n === 2) knobXs.push(c.x0 + (c.x1 - c.x0) * 0.3, c.x0 + (c.x1 - c.x0) * 0.7)
        else knobXs.push((c.x0 + c.x1) / 2)
      }
      for (const z of [lay.griddle, lay.charbroiler]) if (z) knobXs.push((z[0] + z[1]) / 2)
      for (const kx of knobXs) {
        cyl('y', kx, (panelZ0 + panelZ1) / 2, 0.09, d, d + 0.07)        // knob skirt
        cyl('y', kx, (panelZ0 + panelZ1) / 2, 0.06, d + 0.07, d + 0.14) // knob grip
      }
      // ovens: one door below the panel, or a large + small pair on 48" and up
      const doorZ0 = toe + 0.08, doorZ1 = panelZ0 - 0.04
      const ovens: [number, number][] = w >= 3.9
        ? [[-w / 2 + 0.04, -w / 2 + 0.04 + (w - 0.12) * 0.6], [-w / 2 + 0.08 + (w - 0.12) * 0.6, w / 2 - 0.04]]
        : [[-w / 2 + 0.04, w / 2 - 0.04]]
      for (const [x0, x1] of ovens) {
        box(x0, x1, front, d - 0.02, doorZ0, doorZ1)                   // oven door
        box(x0 + 0.25, x1 - 0.25, d - 0.02, d, doorZ0 + 0.35, doorZ1 - 0.45)  // window
        const hz = doorZ1 - 0.18
        for (const sx of [x0 + 0.2, x1 - 0.2]) box(sx - 0.03, sx + 0.03, d - 0.02, d + 0.12, hz - 0.03, hz + 0.03)  // standoffs
        cyl('x', d + 0.14, hz, 0.055, x0 + 0.14, x1 - 0.14)            // tubular handle
      }
      // grates + sealed burners
      const yB = RANGE_BACKGUARD_FT * 0.3 + 0.05, yF = front - 0.05
      for (const c of lay.burnerCols) {
        rimWalls(c.x0 + 0.02, c.x1 - 0.02, yB, yF, 0.05, h, h + 0.12)   // grate frame
        box((c.x0 + c.x1) / 2 - 0.025, (c.x0 + c.x1) / 2 + 0.025, yB, yF, h + 0.06, h + 0.12)  // spine
        const ys = c.n === 2 ? [yB + (yF - yB) * 0.27, yB + (yF - yB) * 0.73] : [(yB + yF) / 2]
        for (const by of ys) {
          box(c.x0 + 0.02, c.x1 - 0.02, by - 0.025, by + 0.025, h + 0.06, h + 0.12)   // cross bar
          prism(ellipsePoly((c.x0 + c.x1) / 2, by, 0.2, 0.2, 16), h, h + 0.05)       // burner base
          prism(ellipsePoly((c.x0 + c.x1) / 2, by, 0.13, 0.13, 16), h + 0.05, h + 0.08)  // cap
        }
      }
      if (lay.griddle) {
        const [x0, x1] = lay.griddle
        box(x0, x1, yB, yF, h, h + 0.05)                               // griddle plate
        rimWalls(x0, x1, yB, yF, 0.04, h + 0.05, h + 0.1)              // splash rim
      }
      if (lay.charbroiler) {
        const [x0, x1] = lay.charbroiler
        rimWalls(x0, x1, yB, yF, 0.05, h, h + 0.12)
        for (let i = 1; i < 8; i++) {
          const x = x0 + ((x1 - x0) * i) / 8
          box(x - 0.015, x + 0.015, yB, yF, h + 0.04, h + 0.11)        // broiler bars
        }
      }
      break
    }
    case 'fridge': {
      // KITCHEN APPLIANCES round: a standard French-door refrigerator — two fresh-food
      // doors over a bottom freezer drawer, bar handles on standoffs, a toe grille
      const front = d - 0.1, toe = 0.25
      const seam = toe + (h - toe) * 0.36
      box(-w / 2, w / 2, 0, front, 0, h)                               // cabinet
      box(-w / 2 + 0.05, w / 2 - 0.05, front, d - 0.05, 0.03, toe - 0.03)  // toe grille
      box(-w / 2, -0.01, front, d, seam + 0.02, h - 0.02)              // left door
      box(0.01, w / 2, front, d, seam + 0.02, h - 0.02)                // right door
      box(-w / 2, w / 2, front, d, toe, seam - 0.02)                   // freezer drawer
      const hy = d + 0.1
      for (const hx of [-0.12, 0.12]) {
        const z0 = seam + 0.45, z1 = Math.min(h - 0.35, seam + 0.45 + 3)
        for (const z of [z0, z1]) box(hx - 0.02, hx + 0.02, d, hy, z - 0.03, z + 0.03)
        box(hx - 0.04, hx + 0.04, hy - 0.04, hy + 0.04, z0 - 0.05, z1 + 0.05)  // bar handle
      }
      const fz = seam - 0.28
      for (const sx of [-w / 2 + 0.4, w / 2 - 0.4]) box(sx - 0.03, sx + 0.03, d, hy, fz - 0.02, fz + 0.02)
      cyl('x', hy, fz, 0.04, -w / 2 + 0.3, w / 2 - 0.3)              // freezer handle
      break
    }
    case 'columnFridge': {
      // KITCHEN APPLIANCES round: an integrated column (Sub-Zero style) — one full
      // height panel door flush with the cabinets, a recessed toe grille, and a tall
      // appliance-pull handle on the latch side
      const front = d - 0.06, toe = 0.33
      box(-w / 2, w / 2, 0, front, 0, h)                               // cabinet
      box(-w / 2 + 0.04, w / 2 - 0.04, front, front + 0.02, 0.03, toe - 0.03)  // toe grille
      box(-w / 2 + 0.01, w / 2 - 0.01, front, d, toe, h - 0.01)        // panel door
      // the pull sits on the LATCH side — opposite the hinge (COLUMN FRIDGE HINGE)
      const hx = (p.fridgeHinge === 'right' ? -1 : 1) * (w / 2 - 0.22), hy = d + 0.1, z0 = toe + 1.6, z1 = h - 0.6
      for (const z of [z0, z1]) box(hx - 0.02, hx + 0.02, d, hy, z - 0.03, z + 0.03)  // standoffs
      box(hx - 0.04, hx + 0.04, hy - 0.04, hy + 0.04, z0 - 0.05, z1 + 0.05)  // tall pull
      break
    }
    case 'hoodInsert': {
      // KITCHEN APPLIANCES round: a ventilation insert — the liner shell, its
      // baffle-filter tray and two lights; it hangs at its instance base (the hood's
      // "Height Above Level")
      rimWalls(-w / 2, w / 2, 0, d, 0.06, 0, h)                        // liner shell
      box(-w / 2 + 0.06, w / 2 - 0.06, 0.06, d - 0.06, h - 0.1, h)      // top / blower plate
      const fy0 = 0.15, fy1 = d - 0.3
      const n = Math.max(2, Math.round((w - 0.3) / 1.2))
      const fw = (w - 0.3) / n
      for (let i = 0; i < n; i++) {
        const x0 = -w / 2 + 0.15 + i * fw
        box(x0 + 0.02, x0 + fw - 0.02, fy0, fy1, 0.06, 0.1)            // baffle filters
      }
      for (const lx of [-w / 4, w / 4]) prism(ellipsePoly(lx, d - 0.16, 0.08, 0.08, 12), 0.04, 0.08)  // lights
      break
    }
    // ---- DERIVED HOOKUPS round (2026-10-04): the equipment the hookups called for ----
    case 'microwaveDrawer': {
      const z0 = 34 / 12 - h
      box(-w / 2, w / 2, 0, d - 0.06, z0, z0 + h)                      // body
      box(-w * 0.46, w * 0.46, d - 0.06, d, z0 + 0.05, z0 + h - 0.22)  // drawer front
      box(-w * 0.46, w * 0.46, d - 0.06, d, z0 + h - 0.17, z0 + h - 0.03)  // control strip
      break
    }
    case 'wallOven': {
      const { z0, z1 } = objectZSpan(p)
      const mid = (z0 + z1) / 2
      box(-w / 2, w / 2, 0, d - 0.06, z0, z1)                          // body
      box(-w * 0.45, w * 0.45, d - 0.06, d, z0 + 0.08, mid - 0.04)     // lower door
      box(-w * 0.45, w * 0.45, d - 0.06, d, mid + 0.04, z1 - 0.3)      // upper door
      box(-w * 0.45, w * 0.45, d - 0.06, d, z1 - 0.25, z1 - 0.05)      // controls
      cyl('x', d + 0.08, mid - 0.25, 0.03, -w * 0.35, w * 0.35)         // handles
      cyl('x', d + 0.08, z1 - 0.45, 0.03, -w * 0.35, w * 0.35)
      break
    }
    case 'beverageCenter':
    case 'iceMaker': {
      box(-w / 2, w / 2, 0, d - 0.06, 0.08, h)                         // body
      box(-w / 2 + 0.05, w / 2 - 0.05, 0.1, d - 0.15, 0, 0.08)         // toe kick
      box(-w * 0.45, w * 0.45, d - 0.06, d, 0.12, h - 0.05)            // door
      if (kind === 'beverageCenter') box(-w * 0.38, w * 0.38, d - 0.03, d + 0.005, 0.3, h - 0.25)  // glass lite
      break
    }
    case 'builtInAppliance': {
      // CASEWORK STUDIO stage B (2026-10-09): a built-in appliance at its opening's size, its look
      // from its unit (caseworkAppliances.ts). The local base is the opening's bottom.
      const look = applianceUnitOf(p.applianceUnit)?.look ?? 'oven'
      const FT = 0.0625   // the cabinet's 3/4" front
      if (look === 'panel') {
        // PANEL-READY: the body sits behind the cabinet's own front (the cabinet builds the panel
        // in its profile, with its hardware); an undercounter unit with no toe kick in front
        // shows its own grille at the floor
        // the body is a real LINER open at the front — the cabinet's own door / drawer fronts close
        // it, and opening one (walk E, the Studio) shows the inside the appliance really has
        const kind = String(p.applianceKind || '')
        const zb = p.applianceGrille === true ? CASEWORK_TOE_FT : 0
        const x0 = -w / 2 + 0.01, x1 = w / 2 - 0.01, yf = d - FT - 0.005, t = 0.08
        box(x0, x1, 0, t, zb, h)                       // back
        box(x0, x0 + t, 0, yf, zb, h)                  // sides
        box(x1 - t, x1, 0, yf, zb, h)
        box(x0, x1, 0, yf, zb, zb + t)                 // floor
        box(x0, x1, 0, yf, h - t, h)                   // top
        box(x0 + t, x1 - t, yf - 0.06, yf, h - 0.3, h - t)   // control strip / light band
        const ix0 = x0 + t, ix1 = x1 - t, iz0 = zb + t, iz1 = h - t
        const shelf = (z: number, depth = yf - 0.15) => box(ix0, ix1, t, depth, z, z + 0.03)
        const bin = (za: number, zz: number, yy = yf - 0.08) => {   // a pull-out crisper / basket
          box(ix0 + 0.03, ix1 - 0.03, t + 0.05, yy, za, za + 0.03)
          box(ix0 + 0.03, ix0 + 0.06, t + 0.05, yy, za, zz)
          box(ix1 - 0.06, ix1 - 0.03, t + 0.05, yy, za, zz)
          box(ix0 + 0.03, ix1 - 0.03, yy - 0.03, yy, za, zz)
        }
        if (/fridge|ucFridge/i.test(kind) && kind !== 'fridgeDrawers') {
          // refrigerator: crisper drawers at the bottom, glass shelves above
          bin(iz0 + 0.05, iz0 + 0.75)
          for (let z = iz0 + 1.1; z < iz1 - 0.6; z += 1.15) shelf(z)
        } else if (/freezer|fridgeDrawers|dishDrawers/i.test(kind)) {
          // freezer / refrigerator drawers / dishwasher drawers: baskets stacked in the liner
          const n = kind === 'columnFreezer' ? 3 : 2
          const span = (iz1 - iz0) / n
          for (let k = 0; k < n; k++) bin(iz0 + k * span + 0.08, iz0 + (k + 0.75) * span)
          if (kind === 'columnFreezer') shelf(iz0 + span * 2.9)
        } else if (kind === 'wineColumn' || kind === 'wineUnder' || kind === 'beverage') {
          for (let z = iz0 + 0.2; z < iz1 - 0.2; z += 0.33) shelf(z, yf - 0.25)
        } else if (kind === 'dishwasher') {
          // two racks and the spray arm
          for (const z of [iz0 + 0.4, iz0 + 1.45]) bin(z, z + 0.45)
        } else if (kind === 'iceMaker') {
          bin(iz0 + 0.1, iz0 + 1.2)
        }
        if (p.applianceGrille === true) {
          box(-w / 2 + 0.02, w / 2 - 0.02, 0, d - 0.2, 0, CASEWORK_TOE_FT)          // plinth
          box(-w / 2 + 0.04, w / 2 - 0.04, d - 0.2, d - 0.17, 0.03, CASEWORK_TOE_FT - 0.03)  // grille face
          for (let x = -w / 2 + 0.12; x < w / 2 - 0.08; x += 0.12) box(x - 0.012, x + 0.012, d - 0.17, d - 0.15, 0.05, CASEWORK_TOE_FT - 0.05)
        }
        break
      }
      const front = d - 0.06
      const pullX = (y: number, z: number, a: number, b: number) => {
        for (const x of [a + 0.12, b - 0.12]) box(x - 0.02, x + 0.02, d, y, z - 0.025, z + 0.025)  // standoffs
        cyl('x', y + 0.03, z, 0.035, a, b)
      }
      const pullZ = (x: number, y: number, z0: number, z1: number) => {
        for (const z of [z0 + 0.12, z1 - 0.12]) box(x - 0.025, x + 0.025, d, y, z - 0.02, z + 0.02)
        box(x - 0.035, x + 0.035, y - 0.035, y + 0.035, z0, z1)
      }
      const door = (x0: number, x1: number, z0: number, z1: number) => box(x0, x1, front, d, z0, z1)
      const lite = (x0: number, x1: number, z0: number, z1: number) => box(x0, x1, d - 0.012, d + 0.004, z0, z1)   // glass, a hair proud
      const g = 0.012   // the reveal between doors
      if (look === 'fridgeOU' || look === 'fridgeFD' || look === 'fridgeSBS') {
        // BUILT-IN REFRIGERATOR: stainless, the grille across the top (it stands on the floor, no
        // toe kick in front of it)
        const top = h - 11 / 12
        box(-w / 2, w / 2, 0, d - 0.08, 0, h)                         // cabinet
        box(-w / 2 + 0.03, w / 2 - 0.03, d - 0.08, d - 0.04, top + 0.03, h - 0.03)   // top grille
        for (let x = -w / 2 + 0.1; x < w / 2 - 0.06; x += 0.1) box(x - 0.01, x + 0.01, d - 0.04, d - 0.02, top + 0.08, h - 0.08)
        box(-w / 2, w / 2, d - 0.08, d - 0.06, 0, top)                 // door plane behind the doors
        const hy = d + 0.14
        if (look === 'fridgeOU') {
          const seam = top * 0.33
          door(-w / 2, w / 2, seam + g, top - g)
          door(-w / 2, w / 2, 0.08, seam - g)
          pullZ(w / 2 - 0.25, hy, seam + 0.6, top - 0.6)
          pullX(hy, seam - 0.3, -w / 2 + 0.3, w / 2 - 0.3)
        } else if (look === 'fridgeFD') {
          const seam = top * 0.36
          door(-w / 2, -g, seam + g, top - g)
          door(g, w / 2, seam + g, top - g)
          door(-w / 2, w / 2, 0.08, seam - g)
          pullZ(-0.14, hy, seam + 0.5, top - 0.5)
          pullZ(0.14, hy, seam + 0.5, top - 0.5)
          pullX(hy, seam - 0.3, -w / 2 + 0.3, w / 2 - 0.3)
        } else {
          const xs = -w / 2 + w * 0.42
          door(-w / 2, xs - g, 0.08, top - g)
          door(xs + g, w / 2, 0.08, top - g)
          pullZ(xs - 0.16, hy, 1, top - 0.8)
          pullZ(xs + 0.16, hy, 1, top - 0.8)
        }
        break
      }
      box(-w / 2, w / 2, 0, front, 0, h)                              // body
      const hy = d + 0.08
      if (look === 'oven' || look === 'ovenSmall') {
        // a wall oven / speed oven / steam oven: control strip over a drop-down door with a window
        const ctl = look === 'oven' ? 0.32 : 0.28
        box(-w / 2, w / 2, front, d, h - ctl, h)                      // control strip
        door(-w / 2, w / 2, 0, h - ctl - g)
        lite(-w * 0.34, w * 0.34, 0.25, h - ctl - 0.5)
        pullX(hy, h - ctl - 0.18, -w * 0.4, w * 0.4)
        for (const kx of [-w * 0.3, w * 0.3]) prism(ellipsePoly(kx, d + 0.03, 0.06, 0.06, 12).map(([x, y]) => [x, y] as Pt2), h - ctl / 2 - 0.06, h - ctl / 2 + 0.06)
      } else if (look === 'ovenDouble') {
        const ctl = 0.32, mid = (h - ctl) / 2
        box(-w / 2, w / 2, front, d, h - ctl, h)
        door(-w / 2, w / 2, 0, mid - g)
        door(-w / 2, w / 2, mid + g, h - ctl - g)
        lite(-w * 0.34, w * 0.34, 0.25, mid - 0.45)
        lite(-w * 0.34, w * 0.34, mid + 0.25, h - ctl - 0.45)
        pullX(hy, mid - 0.18, -w * 0.4, w * 0.4)
        pullX(hy, h - ctl - 0.18, -w * 0.4, w * 0.4)
      } else if (look === 'microwave') {
        // a built-in microwave in its trim kit: the trim frame, a side-hinged door, the keypad
        box(-w / 2, w / 2, front, d, h - 0.12, h)
        box(-w / 2, w / 2, front, d, 0, 0.12)
        box(-w / 2, -w / 2 + 0.12, front, d, 0.12, h - 0.12)
        box(w / 2 - 0.12, w / 2, front, d, 0.12, h - 0.12)
        const kx = w / 2 - 0.12 - 0.42
        door(-w / 2 + 0.14, kx - g, 0.14, h - 0.14)
        lite(-w / 2 + 0.3, kx - 0.3, 0.3, h - 0.3)
        box(kx + g, w / 2 - 0.14, front, d - 0.01, 0.14, h - 0.14)    // keypad
        pullZ(kx - 0.1, hy, 0.4, h - 0.4)
      } else if (look === 'microwaveDrawer') {
        box(-w / 2, w / 2, front, d, h - 0.17, h)                     // control strip
        door(-w / 2, w / 2, 0, h - 0.17 - g)
        lite(-w * 0.3, w * 0.3, 0.2, h - 0.45)
        pullX(hy, h - 0.3, -w * 0.4, w * 0.4)
      } else if (look === 'drawer') {
        // a warming / cup-warming / vacuum-seal drawer: one stainless front, a bar pull
        door(-w / 2, w / 2, 0, h)
        pullX(hy, h - Math.min(0.25, h / 3), -w * 0.35, w * 0.35)
      } else if (look === 'coffee') {
        // a plumbed coffee system: the front with its dispensing niche, the bean / water door over
        const nz0 = 0.25, nz1 = h * 0.62, nx = w * 0.18
        door(-w / 2, w / 2, nz1, h)                                   // upper front
        door(-w / 2, -nx, 0, nz1)
        door(nx, w / 2, 0, nz1)
        door(-nx, nx, 0, nz0)                                         // drip tray
        box(-0.04, 0.04, front - 0.25, front - 0.1, nz1 - 0.15, nz1)   // spout
        box(-w * 0.2, w * 0.2, d - 0.012, d + 0.004, h - 0.35, h - 0.15)  // display
      } else if (look === 'wine') {
        // glass door in a frame (wine storage, beverage center)
        door(-w / 2, -w / 2 + 0.12, 0, h); door(w / 2 - 0.12, w / 2, 0, h)
        door(-w / 2 + 0.12, w / 2 - 0.12, 0, 0.18); door(-w / 2 + 0.12, w / 2 - 0.12, h - 0.18, h)
        lite(-w / 2 + 0.12, w / 2 - 0.12, 0.18, h - 0.18)
        for (let z = 0.6; z < h - 0.3; z += 0.6) box(-w / 2 + 0.14, w / 2 - 0.14, 0.2, front - 0.05, z, z + 0.05)   // racks behind
        pullZ(w / 2 - 0.2, hy, Math.min(1, h * 0.3), h - Math.min(1, h * 0.3))
      }
      break
    }
    case 'outdoorGrill': {
      box(-w / 2, w / 2, 0, d, 0, h * 0.55)                            // firebox
      box(-w / 2 + 0.05, w / 2 - 0.05, 0.1, d - 0.05, h * 0.55, h)     // hood
      box(-w * 0.45, w * 0.45, d, d + 0.08, h * 0.2, h * 0.45)         // control panel
      cyl('x', d + 0.18, h * 0.62, 0.03, -w * 0.4, w * 0.4)             // hood handle
      break
    }
    case 'laundrySink': {
      const rim = h
      for (const [lx, ly] of [[-w / 2 + 0.08, 0.08], [w / 2 - 0.08, 0.08], [-w / 2 + 0.08, d - 0.08], [w / 2 - 0.08, d - 0.08]]) {
        box(lx - 0.05, lx + 0.05, ly - 0.05, ly + 0.05, 0, rim - 1.1)  // legs
      }
      box(-w / 2, w / 2, 0, d, rim - 1.15, rim - 1.05)                 // tub floor
      rimWalls(-w / 2, w / 2, 0, d, 0.08, rim - 1.15, rim)             // tub
      cyl('y', 0, rim + 0.5, 0.04, 0.05, 0.45)                         // faucet spout
      break
    }
    case 'hoseBib': {
      const { z0, z1 } = objectZSpan(p)
      const zc = (z0 + z1) / 2
      box(-0.1, 0.1, 0, 0.03, zc - 0.1, zc + 0.1)                      // flange
      cyl('y', 0, zc, 0.04, 0.03, 0.3)                                 // body
      box(-0.03, 0.03, 0.26, 0.3, zc - 0.18, zc)                       // spout down
      box(-0.12, 0.12, 0.12, 0.16, zc + 0.04, zc + 0.08)               // handle
      break
    }
    case 'dishwasher': {
      box(-w / 2, w / 2, 0, d - 0.08, 0, h)                            // body
      box(-w * 0.46, w * 0.46, d - 0.08, d, 0.1, h - 0.05)             // front panel (seam)
      break
    }
    case 'washer':
    case 'dryer': {
      box(-w / 2, w / 2, 0, d - 0.05, 0, h)                            // body
      box(-w * 0.4, w * 0.4, d - 0.05, d, h * 0.55, h * 0.68)          // control panel lip
      break
    }
    case 'carSedan':
    case 'carSUV': {
      glassB = new MeshBuilder()
      buildVehicle(kind, w, d, h, b, glassB)
      break
    }
    // SITE FURNISHINGS (2026-10-06): a round trampoline — its net on the glass channel
    // (2026-10-06, Matt: "a rectangular format trampoline, too")
    case 'trampoline':
    case 'trampolineRect': {
      glassB = new MeshBuilder()
      buildTrampoline(kind, w, d, h, b, glassB, p.trampolineNet !== false)
      break
    }
    // FIRE PIT (2026-10-19): stone body, polished concrete cap, fire media — each part in
    // its own material, the balls' outlines added to the edges (firePit.ts)
    case 'firePit': {
      const mat = (v: unknown) => (typeof v === 'string' && v ? v : null)
      const fp = buildFirePit(w, d, h, b, { media: String(p.pitMedia || 'balls'), stoneMaterial: mat(p.pitStoneMaterial), capMaterial: mat(p.pitCapMaterial) })
      solidGroups = fp.groups
      customEdges = fp.edges
      break
    }
    // BUBBLER FOUNTAIN (2026-10-19): the limestone drum, its water on the glass channel
    case 'fountain': {
      glassB = new MeshBuilder()
      const fm = p.fountainMaterial
      const fo = buildFountain(w, d, h, b, glassB, { material: typeof fm === 'string' && fm ? fm : null })
      solidGroups = fo.groups
      customEdges = fo.edges
      break
    }
    // YARD PIECES (2026-10-19): trash cart, patio umbrellas, chaise — each part in its colour
    case 'trashCart': {
      const r = buildTrashCart(w, d, h, b, p.binStream)
      solidGroups = r.groups; customEdges = r.edges
      break
    }
    case 'umbrellaOct':
    case 'umbrellaRect': {
      const r = buildUmbrella(kind === 'umbrellaRect', w, d, h, b, p.canopyColor)
      solidGroups = r.groups; customEdges = r.edges
      break
    }
    case 'chaise': {
      const r = buildChaise(w, d, b, { back: p.chaiseBack, cushion: p.cushionColor, frame: p.chaiseFrame })
      solidGroups = r.groups; customEdges = r.edges
      break
    }
    // SITE UTILITIES (2026-10-07): the gas meter set and the water meter box — each part
    // in its own colour unless the type picks a surface material
    case 'gasMeter': {
      const g = buildGasMeter(w, d, h, b, p.gasSeismicValve === true, !!p.surfaceMaterial)
      solidGroups = g.groups
      glassB = g.glass
      break
    }
    case 'waterMeterBox':
      solidGroups = buildWaterMeterBox(w, d, h, b, !!p.surfaceMaterial).groups
      break
    // PLANTING: the simple stand-in (sections, elevations, picking, collision) — the
    // viewer draws the stylized swaying plant over it from `plant.species`
    case 'plant':
      buildPlantProxy(p.plantSpecies, w, d, h, b)
      break
    case 'washerDryerCombo': {
      box(-w / 2, w / 2, 0, d - 0.05, 0, h)                            // body
      box(-w * 0.4, w * 0.4, d - 0.05, d, h * 0.5, h * 0.58)           // mid-stack control seam
      break
    }
    // recessedCan/pendant/flushMount moved to the `fixture` category's own
    // generate3d.ts (CEILING HOSTING round, 2026-08-23 — Matt: "Lights should
    // be under electrical fixtures, not objects").
    default:
      box(-w / 2, w / 2, 0, d, 0, h)
  }

  const solid = b.build()
  const sm = el.typeParams.surfaceMaterial
  return {
    key, solid, glass: glassB ? glassB.build() : null, edges: kind === 'plant' ? new Float32Array(0) : customEdges ?? featureEdges(solid),
    solidMaterialId: typeof sm === 'string' && sm ? sm : null,
    ...(kind === 'plant' ? { plant: { species: String(p.plantSpecies || 'olive'), w, d, h } } : {}),
    ...(solidGroups.length ? { solidGroups } : {}),
  }
}

/**
 * SURFACE HOSTING round (2026-08-24): the host's own top surface, in absolute
 * world feet — floor (its own top, the "hangs below its top" convention
 * shared with slabCfg's floorZ), casework (caseworkTopZ — the countertop's own
 * top when present, else the cabinet body's), or another object (objectTopZ,
 * ONE level deep only — a host object's own z is read at its plain unhosted
 * position, never its own host, so this can never recurse or cycle). Any
 * other host category (shouldn't reach here — the schema's own
 * `hostsInto` list is the gate) degrades to null, which falls back to the
 * ordinary level+heightOffsetFt path below exactly as if unhosted. EXPORTED
 * so the frontend's pick config (pickConfigs.js#objectCfg) reads the exact
 * same derivation for z-bounds instead of a second hand-copied dispatcher.
 */
export function hostSurfaceTopZ(
  host: ResolvedElement,
  /** PLANT HOSTING (2026-10-05): a TERRAIN host's height varies — read at this point */
  at?: Pt2 | null,
  byId?: Map<string, ResolvedElement>,
): number | null {
  if (host.category === 'topo') return at ? foundationGradeAt(byId ?? new Map([[host.id as string, host]]), at) : null
  if (host.category === 'floor') {
    if (!host.level) return null
    return host.level.elevationFt + ((host.params.heightOffsetFt as number) || 0)
  }
  if (host.category === 'casework') return caseworkTopZ(host)
  if (host.category === 'object') return objectTopZ(host)
  return null
}

/**
 * The per-element solid: meshless — the body rides the instancing pipeline via
 * `openingInstances` (one prototype per type, a transform per element). NO projLines:
 * the plan draws the authored symbol, never this geometry.
 */
/** where an object stands for its host's height: a plant's middle (its anchor is its
 *  back edge), anything else its anchor */
export function objectStandPoint(el: ResolvedElement): Pt2 | null {
  const anchor = objectAnchorPoint(el)
  if (!anchor) return null
  // SITE UTILITIES: the water meter box reads the grade at its middle too
  if (!['plant', 'waterMeterBox'].includes(String(el.typeParams.objectKind || ''))) return anchor
  const d = (el.typeParams.depthFt as number) || 2
  const r = (((el.params.rotationDeg as number) || 0) * Math.PI) / 180
  return [anchor[0] - Math.sin(r) * (d / 2), anchor[1] + Math.cos(r) * (d / 2)]
}

/**
 * Where an object's body starts (world feet) and the surface it sits on (null = none).
 * SURFACE HOSTING round: a host's surface wins over the level — the object follows the
 * slab, counter or ground under it. APPLIANCE HEIGHT OFFSET (2026-10-15, Armando: "nothing
 * happens when I input a value for the height offset of an appliance"): an object standing
 * on a FLOOR or the TERRAIN still lifts by its own Height Offset above that surface (a wall
 * oven raised into its cabinet, a washer on a pedestal) — before this the floor host
 * swallowed the typed value. Counter-seated kinds (sinks, faucets, cooktops) keep riding the
 * counter top exactly. Shared by objectSolid and the app's pick bounds (pickConfigs.js).
 */
export function objectBaseZ(el: ResolvedElement, host: ResolvedElement | null = null, byId?: Map<string, ResolvedElement>): { zInst: number; hostTop: number | null } {
  const off = (el.params.heightOffsetFt as number) || 0
  const hostTop = host ? hostSurfaceTopZ(host, host.category === 'topo' ? objectStandPoint(el) : null, byId) : null
  if (hostTop == null) return { zInst: (el.level?.elevationFt ?? 0) + off, hostTop: null }
  const lifts = host!.category === 'floor' || host!.category === 'topo'
  return { zInst: hostTop + (lifts ? off : 0), hostTop }
}

export function objectSolid(el: ResolvedElement, host: ResolvedElement | null = null, byId?: Map<string, ResolvedElement>): ElementSolid | null {
  if (!el.level) return null
  const anchor = objectAnchorPoint(el)
  if (!anchor) return null
  // SURFACE HOSTING round: hostId wins over the level when present, mirroring
  // the ceiling-fixture precedent — a hosted object's real height is the surface
  // underneath it, not a flat number that goes stale the moment that surface moves.
  const { zInst, hostTop } = objectBaseZ(el, host, byId)
  const rotZ = (((el.params.rotationDeg as number) || 0) * Math.PI) / 180
  // SINKS ON COUNTERS (2026-10-01, Matt): only a COUNTER-like host (casework, another
  // object) seats a sink's rim at its top — a FLOOR host keeps the rim height above that
  // floor (Foulger's floor-hosted sinks were buried in the slab, bowl below the floor)
  const onCounter = !!host && hostTop != null && (host.category === 'casework' || host.category === 'object')
  const { z0, z1 } = objectZSpan(el.typeParams, onCounter)
  // the sink body is built with its rim at `rimHeightFt` above the instance base, so a
  // counter-seated sink's base drops by that rim — the rim lands ON the counter top
  // (it used to float a whole rim height above the counter)
  const kind = String(el.typeParams.objectKind || '')
  // PLUMBING STYLES: a styled fixture drops by its rim only when its style sits on a counter
  // (undermount / apron / vessel basins, deck faucets, a rim filler) — a pedestal or a
  // washstand stands on the floor whatever it's hosted to
  const styled = isStyledPlumbingKind(kind) ? plumbingStyleOf(kind, el.typeParams) : null
  const rimKind = styled ? plumbingOnCounter(kind, styled) : (kind === 'kitchenSink' || kind === 'bathSink' || kind === 'cooktop')
  const rimDrop = onCounter && rimKind ? ((el.typeParams.rimHeightFt as number) || 3) : 0
  // PLUMBING STYLES: an undermount or apron sink seated in a countertop cuts its opening
  // (pipeline.ts#joinCounterSolids subtracts it from the welded top)
  let counterCut: ElementSolid['counterCut']
  if (host?.category === 'casework' && hostTop != null && (styled || kind === 'cooktop')) {
    const cut = styled ? plumbingCounterCutIn(kind, styled, plumbingWidthIn(styled, el.typeParams)) : cooktopCounterCutIn(el.typeParams as Record<string, ParamValue>)

    if (cut) {
      const c = Math.cos(rotZ), s = Math.sin(rotZ)
      counterCut = {
        levelId: String(el.levelId),
        poly: cut.map(([x, y]) => [anchor[0] + (x / 12) * c - (y / 12) * s, anchor[1] + (x / 12) * s + (y / 12) * c] as Pt2),
      }
    }
  }
  return {
    ...(counterCut ? { counterCut } : {}),
    elementId: el.id,
    category: 'object',
    outlines: [],
    zBase: zInst + z0,
    zTop: zInst + z1,
    materialId: 'default',
    groupKey: `object|${el.levelId}|${el.id}`,
    pieces: [],
    placements: [],
    openingInstances: [{ opening: el, pos: [anchor[0], anchor[1], zInst - rimDrop], rotZ }],
    meshOnly: true,
  }
}
